/**
 * Frontend IAM access profile — backend `/api/v1/auth/me` is the source of truth.
 * JWT roles are only used when IAM is disabled, bearer auth is not required, or
 * explicit dev/report-only fallback mode allows profile-error fallback.
 */

import { hasSmartContext } from '../api/smartApi';

import { fetchWithAuth } from '../api/fetchWithAuth';
import { getMarchHttpApiBase, isIamDisabled, requiresMarchAgentBearer } from '../api/marchApiEnv';
import { getAuthScopedStorage } from './tokenStore';
import {
  broadcastIamProfileCleared,
  broadcastIamProfileRefreshed,
  subscribeIamProfileSyncEvents,
} from './iamProfileSync';
import {
  canAccessAdmin as rbacCanAccessAdmin,
  canAccessClinicalWorkspace as rbacCanAccessClinicalWorkspace,
} from './rbac';

export type PanelKey = 'admin' | 'specialty';

export type FrontendAuthzMode = 'disabled' | 'report_only' | 'enforce';

export type IamAccessProfile = {
  user: {
    id: string;
    username?: string;
    email?: string;
  };
  permissions: string[];
  available_panels: PanelKey[];
  default_panel: PanelKey | 'none';
  can_access_admin: boolean;
  can_access_chat: boolean;
  /**
   * The single tenant this user is scoped to (canonical `march-health-dev` in
   * dev). The deployment is single-tenant, so this is derived from the backend
   * identity — never chosen in the UI. Empty string when unknown (unauthenticated).
   */
  tenant_id: string;
  iam_policy_version?: number;
  access_denied_reason?: string;
};

export type IamAccessState = {
  status: 'idle' | 'loading' | 'ready' | 'error';
  profile: IamAccessProfile | null;
  usedRoleFallback: boolean;
};

const IAM_PROFILE_STORAGE_KEY = 'march_iam_access_profile_v1';
const AUTH_ME_TIMEOUT_MS = 15_000;

const PERMISSION_PANEL_ADMIN = 'panel:admin:access';
const PERMISSION_PANEL_SPECIALTY = 'panel:specialty:access';
const PERMISSION_CHAT = 'chat:access';
/**
 * Canonical "grants everything" wildcard. A profile holding this satisfies any
 * permission check — matching the backend (march.auth.permissions,
 * auth_routes._has_permission, permission_model._evaluate_resource_access) and
 * ABAC layers, which all honor the same wildcard. Full-access admin roles
 * (e.g. org_admin) hold it, so no admin route/menu/action is ever hidden.
 */
const WILDCARD_PERMISSION = 'admin:all';

let cachedProfile: IamAccessProfile | null = null;
let cachedAt = 0;
let cachedPolicyVersion: number | null = null;
let loadPromise: Promise<IamAccessProfile | null> | null = null;
let accessState: IamAccessState = { status: 'idle', profile: null, usedRoleFallback: false };
const listeners = new Set<(state: IamAccessState) => void>();

const CACHE_TTL_MS = 30_000;

export function isIamAccessCacheExpired(): boolean {
  if (!cachedProfile) return true;
  return Date.now() - cachedAt >= CACHE_TTL_MS;
}

export async function refreshIamAccessOnPanelSwitch(): Promise<void> {
  if (!isIamAccessCacheExpired()) return;
  await refreshIamAccessProfile();
}

if (typeof window !== 'undefined') {
  subscribeIamProfileSyncEvents((event) => {
    if (
      event.type === 'iam-profile-cleared' ||
      event.type === 'auth-logout' ||
      event.type === 'auth-token-refresh-failed'
    ) {
      clearIamAccessProfile(event.type, { broadcast: false });
      return;
    }
    if (event.type === 'iam-profile-refreshed') {
      if (
        event.policyVersion != null &&
        cachedPolicyVersion != null &&
        event.policyVersion !== cachedPolicyVersion
      ) {
        cachedProfile = null;
        cachedAt = 0;
        loadPromise = null;
      }
    }
  });
}

function notify() {
  for (const listener of listeners) {
    listener(accessState);
  }
}

function setAccessState(next: IamAccessState) {
  accessState = next;
  notify();
}

export function getFrontendAuthzMode(): FrontendAuthzMode {
  const raw = (import.meta.env.VITE_MARCH_AUTHZ_ENFORCEMENT ?? 'enforce').toString().trim().toLowerCase();
  if (raw === 'disabled' || raw === 'report_only' || raw === 'enforce') {
    return raw;
  }
  return 'enforce';
}

export function roleFallbackAllowedOnProfileError(): boolean {
  if (isIamDisabled() || !requiresMarchAgentBearer()) {
    return true;
  }
  const mode = getFrontendAuthzMode();
  return mode === 'disabled' || mode === 'report_only';
}

export function emptyAccessProfile(): IamAccessProfile {
  return {
    user: { id: '' },
    permissions: [],
    available_panels: [],
    default_panel: 'none',
    can_access_admin: false,
    can_access_chat: false,
    tenant_id: '',
  };
}

export function subscribeIamAccess(listener: (state: IamAccessState) => void): () => void {
  listeners.add(listener);
  listener(accessState);
  return () => listeners.delete(listener);
}

export function getIamAccessState(): IamAccessState {
  return accessState;
}

export function getCachedIamAccessProfile(): IamAccessProfile | null {
  return cachedProfile;
}

export function getCachedIamPolicyVersion(): number | null {
  return cachedPolicyVersion;
}

function isCacheFresh(): boolean {
  if (!cachedProfile) return false;
  return Date.now() - cachedAt < CACHE_TTL_MS;
}

function readPersistedAccessProfile(): void {
  const store = getAuthScopedStorage();
  if (!store) return;
  const raw = store.getItem(IAM_PROFILE_STORAGE_KEY);
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw) as {
      profile?: Record<string, unknown>;
      cachedAt?: number;
      policyVersion?: number | null;
    };
    if (!parsed.profile || typeof parsed.cachedAt !== 'number') return;
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS * 4) {
      store.removeItem(IAM_PROFILE_STORAGE_KEY);
      return;
    }
    const profile = normalizeProfile(parsed.profile);
    cachedProfile = profile;
    cachedAt = parsed.cachedAt;
    cachedPolicyVersion =
      typeof parsed.policyVersion === 'number' ? parsed.policyVersion : profile.iam_policy_version ?? null;
    setAccessState({ status: 'ready', profile, usedRoleFallback: false });
  } catch {
    store.removeItem(IAM_PROFILE_STORAGE_KEY);
  }
}

function persistAccessProfile(): void {
  const store = getAuthScopedStorage();
  if (!store) return;
  if (!cachedProfile) {
    store.removeItem(IAM_PROFILE_STORAGE_KEY);
    return;
  }
  try {
    store.setItem(
      IAM_PROFILE_STORAGE_KEY,
      JSON.stringify({
        profile: cachedProfile,
        cachedAt,
        policyVersion: cachedPolicyVersion,
      }),
    );
  } catch {
    // Ignore quota / private-mode storage failures.
  }
}

if (typeof window !== 'undefined') {
  readPersistedAccessProfile();
}

function normalizeProfile(payload: Record<string, unknown>): IamAccessProfile {
  const userRaw = (payload.user ?? {}) as Record<string, unknown>;
  const id = String(userRaw.id ?? userRaw.user_id ?? '').trim();
  const available = Array.isArray(payload.available_panels)
    ? payload.available_panels.map((p) => String(p).trim()).filter(Boolean)
    : [];
  const panels = available.filter((p): p is PanelKey => p === 'admin' || p === 'specialty');
  const defaultRaw = String(payload.default_panel ?? 'none').trim();
  const default_panel: IamAccessProfile['default_panel'] =
    defaultRaw === 'admin' || defaultRaw === 'specialty' ? defaultRaw : 'none';

  return {
    user: {
      id,
      username: String(userRaw.username ?? '').trim() || undefined,
      email: String(userRaw.email ?? '').trim() || undefined,
    },
    permissions: Array.isArray(payload.permissions)
      ? payload.permissions.map((p) => String(p).trim()).filter(Boolean)
      : [],
    available_panels: panels,
    default_panel,
    can_access_admin: Boolean(payload.can_access_admin),
    can_access_chat: Boolean(payload.can_access_chat),
    tenant_id: String(payload.tenant_id ?? '').trim(),
    iam_policy_version:
      typeof payload.iam_policy_version === 'number' ? payload.iam_policy_version : undefined,
    access_denied_reason:
      typeof payload.access_denied_reason === 'string'
        ? payload.access_denied_reason.trim() || undefined
        : undefined,
  };
}

function roleFallbackProfile(roles: string[]): IamAccessProfile {
  const canAdmin = rbacCanAccessAdmin(roles);
  const canClinical = rbacCanAccessClinicalWorkspace(roles);
  const available_panels: PanelKey[] = [];
  if (canAdmin) available_panels.push('admin');
  if (canClinical) available_panels.push('specialty');

  let default_panel: IamAccessProfile['default_panel'] = 'none';
  if (canAdmin && canClinical) default_panel = 'specialty';
  else if (canAdmin) default_panel = 'admin';
  else if (canClinical) default_panel = 'specialty';

  const permissions: string[] = [];
  if (canAdmin) permissions.push(PERMISSION_PANEL_ADMIN);
  if (canClinical) {
    permissions.push(PERMISSION_PANEL_SPECIALTY, PERMISSION_CHAT);
  }

  return {
    user: { id: '' },
    permissions,
    available_panels,
    default_panel,
    can_access_admin: canAdmin,
    can_access_chat: canClinical,
    // Role-fallback runs when IAM is disabled or /auth/me is unreachable; the
    // tenant is derived from the backend identity, so it is unknown here. The
    // tenant provider falls back to its configured single-tenant default.
    tenant_id: '',
  };
}

function authMeUrl(): string {
  const base = getMarchHttpApiBase();
  return `${base}/api/v1/auth/me`;
}

function applyRoleFallback(roles: string[]): IamAccessProfile {
  const fallback = roleFallbackProfile(roles);
  cachedProfile = fallback;
  setAccessState({ status: 'ready', profile: fallback, usedRoleFallback: true });
  return fallback;
}

function applyEmptyProfile(status: IamAccessState['status'] = 'error'): IamAccessProfile {
  const empty = emptyAccessProfile();
  cachedProfile = empty;
  setAccessState({ status, profile: empty, usedRoleFallback: false });
  return empty;
}

function resolveProfileError(
  rolesFallback: string[],
  httpStatus?: number,
): IamAccessProfile {
  if (httpStatus === 401 || httpStatus === 403) {
    return applyEmptyProfile('error');
  }
  if (roleFallbackAllowedOnProfileError()) {
    return applyRoleFallback(rolesFallback);
  }
  return applyEmptyProfile('error');
}

export function clearIamAccessProfile(
  reason?: string,
  options?: { broadcast?: boolean },
): void {
  void reason;
  cachedProfile = null;
  cachedAt = 0;
  cachedPolicyVersion = null;
  loadPromise = null;
  const store = getAuthScopedStorage();
  if (store) store.removeItem(IAM_PROFILE_STORAGE_KEY);
  setAccessState({ status: 'idle', profile: null, usedRoleFallback: false });
  if (options?.broadcast !== false) {
    broadcastIamProfileCleared();
  }
}

export async function getIamAccessProfile(options?: {
  rolesFallback?: string[];
  force?: boolean;
}): Promise<IamAccessProfile | null> {
  return fetchIamAccessProfile(options);
}

export async function refreshIamAccessProfile(options?: {
  rolesFallback?: string[];
}): Promise<IamAccessProfile | null> {
  return fetchIamAccessProfile({ ...options, force: true });
}

export async function fetchIamAccessProfile(options?: {
  rolesFallback?: string[];
  force?: boolean;
}): Promise<IamAccessProfile | null> {
  if (!options?.force && isCacheFresh()) {
    return cachedProfile;
  }
  if (!options?.force && loadPromise) {
    return loadPromise;
  }

  const rolesFallback = options?.rolesFallback ?? [];

  if (isIamDisabled() || !requiresMarchAgentBearer()) {
    return applyRoleFallback(rolesFallback);
  }

  if (!cachedProfile) {
    setAccessState({
      status: 'loading',
      profile: null,
      usedRoleFallback: accessState.usedRoleFallback,
    });
  }

  loadPromise = (async () => {
    try {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), AUTH_ME_TIMEOUT_MS);
      let response: Response;
      try {
        response = await fetchWithAuth(authMeUrl(), {
          skip401Retry: false,
          skipStaleAccessHandling: true,
          signal: controller.signal,
        });
      } finally {
        window.clearTimeout(timeoutId);
      }
      if (response.ok) {
        const payload = (await response.json()) as Record<string, unknown>;
        const incomingVersion =
          typeof payload.iam_policy_version === 'number' ? payload.iam_policy_version : null;
        if (
          !options?.force &&
          cachedProfile &&
          incomingVersion != null &&
          cachedPolicyVersion != null &&
          incomingVersion !== cachedPolicyVersion
        ) {
          cachedProfile = null;
          cachedAt = 0;
        }
        let profile = normalizeProfile(payload);

        // Keycloak is the identity source of truth for UAT login roles.
        // If /auth/me is authenticated but the IAM product-access policy is stale,
        // merge role-derived access from the fresh JWT so valid Keycloak users are
        // not blocked from the workspace.
        const fallbackProfile = roleFallbackProfile(rolesFallback);
        const backendDeniedProductAccess =
          profile.access_denied_reason === 'no_product_access' ||
          (!profile.can_access_chat && profile.available_panels.length === 0 && profile.permissions.length === 0);

        if (backendDeniedProductAccess && fallbackProfile.can_access_chat) {
          profile = {
            ...profile,
            permissions: Array.from(new Set([...profile.permissions, ...fallbackProfile.permissions])),
            available_panels: Array.from(new Set([...profile.available_panels, ...fallbackProfile.available_panels])),
            default_panel: profile.default_panel !== 'none' ? profile.default_panel : fallbackProfile.default_panel,
            can_access_admin: profile.can_access_admin || fallbackProfile.can_access_admin,
            can_access_chat: true,
            access_denied_reason: undefined,
          };
        }

        cachedProfile = profile;
        cachedAt = Date.now();
        cachedPolicyVersion = profile.iam_policy_version ?? incomingVersion ?? cachedPolicyVersion;
        setAccessState({ status: 'ready', profile, usedRoleFallback: backendDeniedProductAccess && fallbackProfile.can_access_chat });
        persistAccessProfile();
        broadcastIamProfileRefreshed(cachedPolicyVersion);
        return profile;
      }
      return resolveProfileError(rolesFallback, response.status);
    } catch (error) {
      if (cachedProfile && options?.force) {
        setAccessState({
          status: 'ready',
          profile: cachedProfile,
          usedRoleFallback: accessState.usedRoleFallback,
        });
        return cachedProfile;
      }
      if (error instanceof DOMException && error.name === 'AbortError') {
        return resolveProfileError(rolesFallback);
      }
      return resolveProfileError(rolesFallback);
    } finally {
      loadPromise = null;
    }
  })();

  return loadPromise;
}

function activeProfile(): IamAccessProfile | null {
  return accessState.profile ?? cachedProfile;
}

export function canAccessAdmin(): boolean {
  const profile = activeProfile();
  if (profile) return profile.can_access_admin;
  return false;
}

export function canAccessChat(): boolean {
  const profile = activeProfile();
  if (profile) return profile.can_access_chat;
  return false;
}

export function canAccessClinicalWorkspace(): boolean {
  const profile = activeProfile();
  if (profile) {
    return profile.can_access_chat || hasPanel('specialty');
  }
  return false;
}

export function getAvailablePanels(): PanelKey[] {
  const profile = activeProfile();
  return profile?.available_panels ?? [];
}

export function getDefaultPanel(): IamAccessProfile['default_panel'] {
  const profile = activeProfile();
  return profile?.default_panel ?? 'none';
}

/**
 * The single tenant this user is scoped to, as resolved by the backend. Returns
 * an empty string when the profile has not loaded or the backend did not supply
 * one; callers pair this with a configured single-tenant default.
 */
export function getTenantId(): string {
  return activeProfile()?.tenant_id ?? '';
}

export function hasPermission(permissionKey: string): boolean {
  const profile = activeProfile();
  if (!profile) return false;
  const key = permissionKey.trim().toLowerCase();
  return profile.permissions.some((p) => {
    const held = p.toLowerCase();
    return held === key || held === WILDCARD_PERMISSION;
  });
}

export function hasAnyPermission(permissionKeys: string[]): boolean {
  return permissionKeys.some((permissionKey) => hasPermission(permissionKey));
}

export function hasPanel(panelKey: PanelKey): boolean {
  const profile = activeProfile();
  if (!profile) return false;
  return profile.available_panels.includes(panelKey);
}

export function hasBothPanels(): boolean {
  return hasPanel('admin') && hasPanel('specialty');
}

export function showAdminSwitch(): boolean {
  return canAccessAdmin() && hasPanel('specialty');
}

export function defaultPanelPath(): string {
  // Landing is decided by CONTEXT FIRST, then role.
  //
  // The clinician view answers questions about a patient opened in the EHR, so
  // a SMART launch context is what makes it usable at all -- not a permission.
  // Previously this sent anyone with chat access to /chat, "both → chat, by
  // design", which meant an admin signing in normally landed on a workspace
  // with no patient, and a clinician without a launch landed on a blocked
  // screen that read like a fault.
  //
  //   1. SMART context present  → /chat, whatever the role. An admin who
  //      launches from the EHR is acting as a clinician in that moment.
  //   2. Otherwise admin        → /admin. The panel is useful with no patient.
  //   3. Otherwise chat access  → /launch-required. NOT access-denied: the
  //      permission is real, the launch context is missing, and telling
  //      someone they lack access when they do not is how support tickets are
  //      born.
  //   4. Otherwise              → /access-denied.
  if (hasSmartContext() && (canAccessChat() || hasPanel('specialty'))) return '/chat';
  if (canAccessAdmin()) return '/admin';
  if (canAccessChat() || hasPanel('specialty')) return '/launch-required';
  return '/access-denied';
}

export function getAccessDeniedReason(): string | undefined {
  const profile = activeProfile();
  return profile?.access_denied_reason;
}

/** True only while the first access profile is unavailable (not during background refresh). */
export function isIamAccessLoading(): boolean {
  return accessState.status === 'idle' || (accessState.status === 'loading' && !cachedProfile);
}

export function isIamAccessProfilePending(): boolean {
  return isIamAccessLoading();
}

export async function handleStaleAccessOn403(): Promise<boolean> {
  if (isIamDisabled() || !requiresMarchAgentBearer()) return false;
  await refreshIamAccessProfile();
  const admin = canAccessAdmin();
  const chat = canAccessChat();
  if (!admin && !chat) {
    if (typeof window !== 'undefined' && window.location.pathname !== '/access-denied') {
      window.location.replace('/access-denied');
    }
    return true;
  }
  return false;
}
